import { AiChatbotMessage, MessageRoleEnum, ToolCall } from '@va/dashboard/shared/ai-assistant';
import { CopyIcon2 } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { ChartContext } from '@va/types/charts';
import { RawTimeseriesDataset, RawTimeseriesResponsePayload } from '@va/types/time-series';
import { DoughnutChartDatasetType } from '@va/ui/components/doughnut-chart-card';
import {
  basicExternalChartTooltip,
  CHART_COLORS_ARRAY,
  DoughnutChartV2,
  externalChartTooltip,
  fontWeights,
  LineChart,
  MarkdownRenderer,
  Paragraph,
  PieChart,
  StackedBarChart,
  StyledDiv,
} from '@va/ui/design-system';
import { chartHelpers, formatLineDiagramDateLabels, isDate } from '@va/util/helpers';
import { ChartDataset } from 'chart.js';
import classNames from 'classnames';
import { useMemo } from 'react';

type ChatMessageProps = {
  pulse?: boolean;
} & AiChatbotMessage;

export const ChatMessage = ({ content, role, toolCalls, model, pulse = false }: ChatMessageProps) => {
  const toolCallsToRender = useMemo(
    () => toolCalls?.filter((t) => t.function_name === 'generate_chart') ?? [],
    [toolCalls],
  );

  if (role !== MessageRoleEnum.tool && content === '' && toolCallsToRender.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames('px-10u py-6u bg-white rounded-b-15 flex flex-col gap-4u', {
        'text-primary w-fit max-w-2/3 self-end rounded-tl-15': role === MessageRoleEnum.user,
        'text-neutralBase-93 self-start rounded-tr-15 max-w-full': role === MessageRoleEnum.assistant,
        'w-full': role === MessageRoleEnum.assistant && toolCallsToRender?.length,
        hidden: role === MessageRoleEnum.tool,
        'animate-pulse': pulse,
      })}
    >
      {role !== MessageRoleEnum.tool &&
        (role === MessageRoleEnum.assistant ? (
          <StyledDiv variant={'body'} variantSize={'small'}>
            <MarkdownRenderer content={content} />
          </StyledDiv>
        ) : (
          <Paragraph variant={'body'} variantSize={'small'} className='whitespace-pre-wrap'>
            {content}
          </Paragraph>
        ))}
      {role === MessageRoleEnum.assistant && toolCallsToRender?.map((t) => <ToolCalls key={t.id} toolCall={t} />)}
      {role === MessageRoleEnum.assistant && <AssistantMessageFooter model={model} />}
    </div>
  );
};

const AssistantMessageFooter = ({ model }: { model: string }) => {
  const translate = useTranslate();
  return (
    <div className={'flex flex-row items-center gap-4u'}>
      <CopyIcon2 />
      <div>
        <Paragraph variant={'interface'} variantSize={'regular'} weight={fontWeights.semibold}>
          {translate('aiChatbot.chatMessage.footer.model', { model })}
        </Paragraph>
        <Paragraph variant={'interface'} variantSize={'small'} weight={fontWeights.medium}>
          {translate('aiChatbot.chatMessage.footer.info')}
        </Paragraph>
      </div>
    </div>
  );
};

const ToolCalls = ({ toolCall }: { toolCall: ToolCall }) => {
  const { locale } = useLocale();

  const {
    datasets,
    labels,
    title,
    type,
    timeUnit = 'day',
  } = useMemo(() => {
    try {
      return JSON.parse(toolCall.arguments);
    } catch (e) {
      console.error(`Cannot parse ${toolCall.arguments}`);
      return {};
    }
  }, [toolCall.arguments]);

  const processed = useMemo(
    () => processChartResponse({ datasets, labels, timeUnit, locale, chartType: type }),
    [datasets, labels, locale, timeUnit, type],
  );

  if (type === 'line') {
    return (
      <LineChart
        id={title}
        chartData={{
          datasets: processed?.datasets as ChartDataset<'line'>[],
          labels: processed?.labels,
        }}
        className={'w-full min-h-62.5'}
        tooltipFn={processed?.tooltipFn ? (ctx) => processed.tooltipFn(ctx, title) : undefined}
      />
    );
  }

  if (type === 'bar') {
    return (
      <StackedBarChart
        chartData={{
          datasets: processed?.datasets as ChartDataset<'bar'>[],
          labels: processed?.labels as (string | string[])[],
        }}
        canvasId={title}
        tooltipFn={processed?.tooltipFn ? (ctx) => processed.tooltipFn(ctx, title) : undefined}
      />
    );
  }

  if (type === 'pie') {
    return (
      <PieChart
        id={title}
        datasets={processed.datasets as ChartDataset<'pie'>[]}
        labels={processed.labels}
        className={'max-h-100'}
      />
    );
  }

  if (type === 'doughnut') {
    return (
      <DoughnutChartV2
        canvasId={title}
        datasets={processed.datasets as DoughnutChartDatasetType[]}
        labels={processed.labels}
      />
    );
  }

  return null;
};

function processChartResponse({
  datasets,
  labels,
  timeUnit,
  locale,
  chartType,
}: {
  datasets: Array<ChartDataset | RawTimeseriesDataset>;
  labels: Array<string>;
  timeUnit: string;
  locale: string;
  chartType: string;
}): { datasets: ChartDataset[]; labels: (string | string[])[]; tooltipFn: any } {
  const isCircular = chartType === 'pie' || chartType === 'doughnut';

  if (isDate(labels[0])) {
    const timeSeriesResponse = chartHelpers.buildBaseTimeseriesResponse(
      [{ datasets, labels }] as RawTimeseriesResponsePayload[],
      {
        extractDatasetKey(dataset: RawTimeseriesDataset): string {
          return dataset.label;
        },
        getDatasetLabel(dataset: RawTimeseriesDataset): string {
          return dataset.label;
        },
        getDatasetColor: (dataset, datasetIndex) => CHART_COLORS_ARRAY[datasetIndex! % CHART_COLORS_ARRAY.length],
        unit: timeUnit,
        until: labels.at(-1) ?? '',
      },
    );

    const chartDatasets = chartHelpers.buildChartDatasets({
      currentDatasets: timeSeriesResponse.items,
      previousDatasets: timeSeriesResponse.items,
      hiddenDatasets: [],
      isPreviousEnabled: false,
    });

    const chartLabels = formatLineDiagramDateLabels(timeSeriesResponse.chartLabels, locale);

    return {
      datasets: chartDatasets,
      labels: chartLabels,
      tooltipFn: (ctx: ChartContext, title: string) =>
        externalChartTooltip(ctx, {
          currentTimeIntervals: timeSeriesResponse.timeIntervals ?? [],
          previousTimeIntervals: [],
          showPrevious: false,
          title: title,
          subtitle: '',
          zIndex: 999999,
        }),
    };
  }

  return {
    datasets: datasets.map((d, i) => ({
      ...d,
      backgroundColor: isCircular ? CHART_COLORS_ARRAY : CHART_COLORS_ARRAY[i % CHART_COLORS_ARRAY.length],
      borderColor: isCircular ? CHART_COLORS_ARRAY : CHART_COLORS_ARRAY[i % CHART_COLORS_ARRAY.length],
    })),
    labels,
    tooltipFn: (ctx: ChartContext, title: string) =>
      basicExternalChartTooltip(ctx, {
        title,
        subtitle: '',
        zIndex: 999999,
      }),
  };
}
